'use client';

import { useRouter } from 'next/navigation';
import React, { createContext, useCallback, useContext, useMemo } from 'react';
import type { Location } from '@/features/locations/domain/model/location';
import { useLocations } from '@/features/locations/presentation/hooks/use-locations';
import { NotificationsProvider } from '@/features/notifications/presentation/providers/notifications-provider';

interface DashboardState {
  /** Locales accesibles (selector de local). Carga diferida: vacío hasta que llega la consulta. */
  locations: Location[];
  /** `true` mientras llega la primera respuesta de la lista de locales. */
  isLoadingLocations: boolean;
  /** Local de la ruta, ya validado en el servidor. `null` solo fuera del dashboard de local (rutas admin). */
  location?: Location | null;
  /** Segmento de ruta `[locationId]` (slug). */
  locationId?: string;
  setLocation: (location?: Location | null) => void;
}

export const DashboardContext = createContext<DashboardState>({} as DashboardState);

export const useDashboard = () => {
  const context = useContext(DashboardContext);

  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }

  return context;
};

interface DashboardProviderProps {
  children: React.ReactNode;
  /** Segmento de ruta (slug o UUID) tal como llegó en la URL. */
  locationSlug: string;
  /** Local resuelto y autorizado en el servidor (el layout responde 404 si no existe o no hay acceso). */
  location: Location;
}

export function DashboardProvider({ children, locationSlug, location }: DashboardProviderProps) {
  const router = useRouter();
  const { data: locationsResult, isPending: isLoadingLocations } = useLocations();
  const locations = useMemo(() => locationsResult?.data ?? [], [locationsResult]);

  const setLocation = useCallback(
    (next?: Location | null) => {
      if (!next?.slug) return;

      // Al cambiar de local siempre se aterriza en ventas: la subruta actual puede apuntar a recursos del otro local.
      router.push(`/${next.slug}/sales`);
    },
    [router]
  );

  const value = useMemo<DashboardState>(
    () => ({ locations, isLoadingLocations, location, locationId: locationSlug, setLocation }),
    [locations, isLoadingLocations, location, locationSlug, setLocation]
  );

  return (
    <DashboardContext.Provider value={value}>
      <NotificationsProvider locationId={location.id}>{children}</NotificationsProvider>
    </DashboardContext.Provider>
  );
}
